import { BarChart3, Settings, FileText, Shield, Activity } from 'lucide-react';
import type { CICDPillarDetails } from '../../../types/analysis.types';
import { MetricCard, SectionHeader, DistributionBar, RiskImplicationsPanel } from './shared';
import { safeNum, type PillarDetailsCommonProps } from './shared.utils';

interface Props extends PillarDetailsCommonProps {
  details: CICDPillarDetails;
}

export const CICDDetails: React.FC<Props> = ({ details, metricBg, trackColor, theme, score }) => {
  const d = details as unknown as Record<string, unknown>;

  const hasCI = Boolean(d.hasCI ?? d.has_ci);
  const hasCD = Boolean(d.hasCD ?? d.has_cd);
  const hasTestAutomation = Boolean(d.hasTestAutomation ?? d.has_test_automation);
  const ciPlatform = (d.ciPlatform ?? d.ci_platform ?? null) as string | null;
  const workflowCount = safeNum(d.workflowCount ?? d.workflow_count) ?? 0;

  const capabilities = [
    { label: 'CI Pipeline', present: hasCI, description: 'Automated builds on commits' },
    { label: 'CD Pipeline', present: hasCD, description: 'Automated deployments' },
    { label: 'Test Automation', present: hasTestAutomation, description: 'Automated test runs' },
    {
      label: 'Multiple Workflows',
      present: workflowCount > 1,
      description: 'Separate workflows for different tasks',
    },
  ];

  const getRiskMessage = () => {
    if (!hasCI)
      return 'No CI pipeline means code changes are not automatically validated, increasing the risk of bugs in production.';
    if (!hasTestAutomation)
      return 'Without automated tests, regressions can slip through undetected.';
    if (!hasCD) return 'Manual deployments increase deployment time and risk of human error.';
    return null;
  };

  const riskMessage = getRiskMessage();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard
          label="CI Platform"
          value={ciPlatform || 'None detected'}
          icon={<Settings className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Workflows"
          value={workflowCount}
          icon={<Activity className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="CI Files"
          value={safeNum(d.ciFileCount) ?? 0}
          icon={<FileText className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
      </div>

      <div>
        <SectionHeader
          icon={<BarChart3 className="w-4 h-4" />}
          title="CI/CD Capabilities Distribution"
        />
        <div className="space-y-2">
          {capabilities.map((cap) => (
            <DistributionBar
              key={cap.label}
              label={cap.label}
              value={cap.present ? 1 : 0}
              total={1}
              trackColor={trackColor}
              barColor={cap.present ? '#10b981' : trackColor}
            />
          ))}
        </div>
      </div>

      {riskMessage && score < 70 && (
        <div>
          <SectionHeader icon={<Shield className="w-4 h-4" />} title="Risk Implications" />
          <RiskImplicationsPanel message={riskMessage} theme={theme} />
        </div>
      )}
    </div>
  );
};
